// Reliable notification sound service that works around browser restrictions
class NotificationSoundService {
  private isEnabled: boolean = true;
  private volume: number = 0.5;
  private audioContext: AudioContext | null = null;
  private isUserInteracted: boolean = false;
  private audioElement: HTMLAudioElement | null = null;
  private initPromise: Promise<void> | null = null;
  private persistentSound: boolean = true; // New: Enable persistent sound

  constructor() {
    this.loadPreferences();
    this.setupUserInteractionHandler();
    this.preloadAudioElement();
    this.setupVisibilityHandler();
  }

  private loadPreferences() {
    const storedEnabled = localStorage.getItem("notificationSoundEnabled");
    if (storedEnabled !== null) {
      this.isEnabled = storedEnabled === "true";
    }

    const storedVolume = localStorage.getItem("notificationSoundVolume");
    if (storedVolume !== null) {
      this.volume = Math.max(0, Math.min(1, parseFloat(storedVolume)));
    }

    // Load persistent sound preference
    const storedPersistent = localStorage.getItem(
      "notificationSoundPersistent"
    );
    if (storedPersistent !== null) {
      this.persistentSound = storedPersistent === "true";
    }

    console.log("🔊 Sound preferences loaded:", {
      enabled: this.isEnabled,
      volume: this.volume,
      persistent: this.persistentSound,
    });
  }

  private preloadAudioElement() {
    try {
      // Create a longer, more attention-grabbing notification sound
      const audioDataUrl = this.createNotificationSoundDataUrl();
      this.audioElement = new Audio(audioDataUrl);
      this.audioElement.volume = this.volume;
      this.audioElement.preload = "auto";

      // Load the audio
      this.audioElement.load();

      console.log("🎵 Enhanced audio element preloaded");
    } catch (error) {
      console.warn("Failed to preload audio element:", error);
    }
  }

  private createNotificationSoundDataUrl(): string {
    // Create a longer, more persistent notification sound
    const sampleRate = 22050;
    const duration = 2.5; // Increased from 0.4 to 2.5 seconds
    const samples = Math.floor(sampleRate * duration);
    const buffer = new ArrayBuffer(44 + samples * 2);
    const view = new DataView(buffer);

    // WAV file header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, "RIFF");
    view.setUint32(4, 36 + samples * 2, true);
    writeString(8, "WAVE");
    writeString(12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, "data");
    view.setUint32(40, samples * 2, true);

    // Generate longer, more persistent notification sound pattern
    for (let i = 0; i < samples; i++) {
      const t = i / sampleRate;
      let sample = 0;

      // Pattern: Three sequential beeps with increasing urgency

      // First beep sequence (0-0.8s): Two-tone attention getter
      if (t < 0.8) {
        // High frequency attention tone (1000Hz)
        if (t < 0.3) {
          const envelope = Math.exp(-t * 3) * (1 - t / 0.3);
          sample += Math.sin(2 * Math.PI * 1000 * t) * envelope * 0.6;
        }

        // Lower response tone (650Hz)
        if (t > 0.15 && t < 0.6) {
          const t2 = t - 0.15;
          const envelope = Math.exp(-t2 * 2) * (1 - t2 / 0.45);
          sample += Math.sin(2 * Math.PI * 650 * t2) * envelope * 0.5;
        }
      }

      // Short pause (0.8-1.0s)

      // Second beep sequence (1.0-1.7s): More urgent
      if (t >= 1.0 && t < 1.7) {
        const t3 = t - 1.0;

        // Rapid alternating tones
        if (t3 < 0.35) {
          const freq = Math.sin(t3 * 20) > 0 ? 900 : 1200; // Alternating frequencies
          const envelope = Math.exp(-t3 * 2) * 0.7;
          sample += Math.sin(2 * Math.PI * freq * t3) * envelope;
        }

        // Sustained urgent tone
        if (t3 >= 0.35) {
          const t4 = t3 - 0.35;
          const envelope = Math.exp(-t4 * 3) * 0.6;
          sample += Math.sin(2 * Math.PI * 800 * t4) * envelope;
        }
      }

      // Third beep sequence (1.8-2.5s): Final urgent call
      if (t >= 1.8) {
        const t5 = t - 1.8;

        // Triple beep pattern
        const beepDuration = 0.15;
        const pauseDuration = 0.08;
        const beepCycle = beepDuration + pauseDuration;
        const beepIndex = Math.floor(t5 / beepCycle);
        const beepTime = t5 % beepCycle;

        if (beepIndex < 3 && beepTime < beepDuration) {
          const freq = 1100 - beepIndex * 100; // Descending tones
          const envelope = Math.sin((beepTime / beepDuration) * Math.PI) * 0.8;
          sample += Math.sin(2 * Math.PI * freq * beepTime) * envelope;
        }
      }

      // Add subtle harmonic for richness
      if (t < 2.0) {
        sample += Math.sin(2 * Math.PI * 400 * t) * Math.exp(-t * 2) * 0.1;
      }

      // Apply volume and convert to 16-bit (increased base amplitude)
      const value =
        Math.max(-1, Math.min(1, sample * this.volume * 1.5)) * 32767;
      view.setInt16(44 + i * 2, value, true);
    }

    const blob = new Blob([buffer], { type: "audio/wav" });
    return URL.createObjectURL(blob);
  }

  private setupVisibilityHandler() {
    // Handle page visibility changes
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden && this.isUserInteracted) {
        // Page became visible and user has interacted - ensure audio is ready
        this.initializeAudioContext().catch(console.warn);
      }
    });
  }

  private setupUserInteractionHandler() {
    const handleFirstInteraction = async () => {
      console.log("👆 User interaction detected, initializing audio...");
      this.isUserInteracted = true;

      try {
        // Initialize audio context
        await this.initializeAudioContext();

        // Prepare audio element
        if (this.audioElement) {
          this.audioElement.volume = this.volume;
          // Try to play and immediately pause to "unlock" audio
          const playPromise = this.audioElement.play();
          if (playPromise) {
            playPromise
              .then(() => {
                if (this.audioElement) {
                  this.audioElement.pause();
                  this.audioElement.currentTime = 0;
                }
              })
              .catch(() => {
                // Ignore play errors during initialization
              });
          }
        }

        console.log("✅ Audio initialized after user interaction");
      } catch (error) {
        console.warn("Failed to initialize audio after interaction:", error);
      }

      // Remove listeners after first successful interaction
      document.removeEventListener("click", handleFirstInteraction);
      document.removeEventListener("keydown", handleFirstInteraction);
      document.removeEventListener("touchstart", handleFirstInteraction);
      document.removeEventListener("scroll", handleFirstInteraction);
      document.removeEventListener("mousemove", handleFirstInteraction);
    };

    // Add event listeners for user interaction - including more events
    document.addEventListener("click", handleFirstInteraction, {
      passive: true,
    });
    document.addEventListener("keydown", handleFirstInteraction, {
      passive: true,
    });
    document.addEventListener("touchstart", handleFirstInteraction, {
      passive: true,
    });
    document.addEventListener("scroll", handleFirstInteraction, {
      passive: true,
    });
    document.addEventListener("mousemove", handleFirstInteraction, {
      passive: true,
      once: true,
    });
  }

  private async initializeAudioContext() {
    if (this.audioContext) return;

    try {
      this.audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();

      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }

      console.log("🎵 Audio context initialized:", this.audioContext.state);
    } catch (error) {
      console.warn("Failed to initialize audio context:", error);
    }
  }

  // Method 1: HTML5 Audio (most reliable) - Enhanced for persistence
  private async playHtml5Audio(): Promise<boolean> {
    if (!this.audioElement) {
      console.warn("No audio element available");
      return false;
    }

    try {
      this.audioElement.volume = this.volume;
      this.audioElement.currentTime = 0;

      const playPromise = this.audioElement.play();
      if (playPromise !== undefined) {
        await playPromise;
        console.log("🔊 Enhanced HTML5 audio played successfully (1/3)");

        // If persistent sound is enabled, play 2 more times
        if (this.persistentSound) {
          // Second play after 3 seconds
          setTimeout(async () => {
            if (this.isEnabled && this.audioElement) {
              try {
                this.audioElement.currentTime = 0;
                await this.audioElement.play();
                console.log("🔊 Persistent audio repeat played (2/3)");
              } catch (error) {
                console.warn("Second audio repeat failed:", error);
              }
            }
          }, 3000);

          // Third play after 6 seconds
          setTimeout(async () => {
            if (this.isEnabled && this.audioElement) {
              try {
                this.audioElement.currentTime = 0;
                await this.audioElement.play();
                console.log("🔊 Persistent audio repeat played (3/3)");
              } catch (error) {
                console.warn("Third audio repeat failed:", error);
              }
            }
          }, 6000);
        }

        return true;
      }
    } catch (error) {
      console.warn("Enhanced HTML5 audio failed:", error);
    }

    return false;
  }

  // Method 2: Web Audio API (fallback) - Enhanced pattern
  private async playWebAudio(): Promise<boolean> {
    try {
      await this.initializeAudioContext();

      if (!this.audioContext) {
        return false;
      }

      // Create a more complex, attention-grabbing sound
      const context = this.audioContext;
      const currentTime = context.currentTime;

      // Play multiple overlapping oscillators for richness
      for (let i = 0; i < 3; i++) {
        const oscillator = context.createOscillator();
        const gainNode = context.createGain();
        const filter = context.createBiquadFilter();

        oscillator.connect(filter);
        filter.connect(gainNode);
        gainNode.connect(context.destination);

        // Different frequencies for each oscillator
        const baseFreq = [1000, 650, 400][i];
        oscillator.frequency.setValueAtTime(baseFreq, currentTime);

        // Add frequency modulation for urgency
        oscillator.frequency.linearRampToValueAtTime(
          baseFreq * 1.2,
          currentTime + 0.5
        );
        oscillator.frequency.linearRampToValueAtTime(
          baseFreq * 0.8,
          currentTime + 1.0
        );

        // Filter for warmth
        filter.type = "lowpass";
        filter.frequency.setValueAtTime(2000, currentTime);

        // Complex envelope
        const amplitude = [0.6, 0.4, 0.3][i] * this.volume;
        gainNode.gain.setValueAtTime(0, currentTime);
        gainNode.gain.linearRampToValueAtTime(amplitude, currentTime + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(
          amplitude * 0.7,
          currentTime + 0.8
        );
        gainNode.gain.exponentialRampToValueAtTime(0.001, currentTime + 2.5);

        const startDelay = i * 0.1; // Slight stagger
        oscillator.start(currentTime + startDelay);
        oscillator.stop(currentTime + startDelay + 2.5);
      }

      console.log("🔊 Enhanced Web Audio played successfully");
      return true;
    } catch (error) {
      console.warn("Enhanced Web Audio failed:", error);
      return false;
    }
  }

  // Method 3: System beep (last resort) - Enhanced
  private playSystemBeep(): boolean {
    try {
      // Create multiple short beeps for persistence
      const beepCount = this.persistentSound ? 9 : 3; // 3 sets of 3 beeps if persistent
      for (let i = 0; i < beepCount; i++) {
        setTimeout(() => {
          const context = new AudioContext();
          const oscillator = context.createOscillator();
          const gain = context.createGain();

          oscillator.connect(gain);
          gain.connect(context.destination);

          oscillator.frequency.value = 800 + (i % 3) * 200;
          gain.gain.value = 0.3 * this.volume;

          oscillator.start(0);
          oscillator.stop(0.2);
        }, Math.floor(i / 3) * 3000 + (i % 3) * 300); // Group into sets of 3 with 3-second intervals
      }

      console.log("🔊 Enhanced system beep played");
      return true;
    } catch (error) {
      console.warn("Enhanced system beep failed:", error);
      return false;
    }
  }

  // Main play method - tries multiple approaches with persistence
  public async playNotificationSound(): Promise<boolean> {
    if (!this.isEnabled) {
      console.log("🔇 Notification sound is disabled");
      return false;
    }

    console.log("🔊 Attempting to play enhanced notification sound...");

    // If user hasn't interacted yet, show a warning but still try
    if (!this.isUserInteracted) {
      console.warn(
        "⚠️ User hasn't interacted with the page yet - audio may be blocked"
      );
    }

    // Try different methods in order of reliability
    let success = false;

    // Method 1: HTML5 Audio
    success = await this.playHtml5Audio();
    if (success) return true;

    // Method 2: Web Audio API
    success = await this.playWebAudio();
    if (success) return true;

    // Method 3: System beep
    success = this.playSystemBeep();
    if (success) return true;

    console.error("❌ All enhanced audio methods failed");
    return false;
  }

  // Force play (for testing - bypasses user interaction check)
  public async forcePlaySound(): Promise<boolean> {
    console.log("🧪 Force playing enhanced sound for testing...");

    // Temporarily enable user interaction flag
    const wasInteracted = this.isUserInteracted;
    this.isUserInteracted = true;

    const result = await this.playNotificationSound();

    // Restore original state
    this.isUserInteracted = wasInteracted;

    return result;
  }

  // Test all audio methods
  public async testSound(): Promise<void> {
    console.log("🧪 Testing all enhanced audio methods...");

    // Force user interaction flag for testing
    this.isUserInteracted = true;

    console.log("Testing Enhanced HTML5 Audio...");
    await this.playHtml5Audio();

    setTimeout(async () => {
      console.log("Testing Enhanced Web Audio API...");
      await this.playWebAudio();

      setTimeout(() => {
        console.log("Testing Enhanced System Beep...");
        this.playSystemBeep();
      }, 1000);
    }, 3000);

    // Also show browser notification
    this.showBrowserNotification(
      "🧪 Enhanced Sound Test",
      "Testing enhanced notification sound - you should hear a longer, more attention-grabbing audio!",
      "/favicon.ico"
    );
  }

  // Enable/disable sound
  public setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
    localStorage.setItem("notificationSoundEnabled", enabled.toString());
    console.log(`🔊 Enhanced sound ${enabled ? "enabled" : "disabled"}`);
  }

  // Get sound enabled state
  public isEnabledSound(): boolean {
    return this.isEnabled;
  }

  // Set persistent sound mode
  public setPersistent(persistent: boolean) {
    this.persistentSound = persistent;
    localStorage.setItem("notificationSoundPersistent", persistent.toString());
    console.log(`🔊 Persistent sound ${persistent ? "enabled" : "disabled"}`);
  }

  // Get persistent sound state
  public isPersistent(): boolean {
    return this.persistentSound;
  }

  // Set volume
  public setVolume(volume: number) {
    this.volume = Math.max(0, Math.min(1, volume));
    localStorage.setItem("notificationSoundVolume", this.volume.toString());

    // Update audio element volume
    if (this.audioElement) {
      this.audioElement.volume = this.volume;
    }

    console.log("🔊 Volume set to:", Math.round(this.volume * 100) + "%");
  }

  // Get current volume
  public getVolume(): number {
    return this.volume;
  }

  // Show browser notification
  public async showBrowserNotification(
    title: string,
    body: string,
    icon?: string
  ): Promise<boolean> {
    console.log("🖥️ Showing browser notification:", { title, body });

    if (!("Notification" in window)) {
      console.warn("Browser does not support notifications");
      return false;
    }

    try {
      let permission = Notification.permission;

      if (permission === "default") {
        permission = await Notification.requestPermission();
      }

      if (permission !== "granted") {
        console.warn("Notification permission denied");
        return false;
      }

      const notification = new Notification(title, {
        body,
        icon: icon || "/favicon.ico",
        tag: "app-notification-" + Date.now(),
        requireInteraction: false,
        silent: true, // We handle our own sound
      });

      setTimeout(() => notification.close(), 8000); // Longer display time

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      return true;
    } catch (error) {
      console.error("Failed to show notification:", error);
      return false;
    }
  }

  // Get audio status for debugging
  public getAudioStatus() {
    return {
      isEnabled: this.isEnabled,
      volume: this.volume,
      persistent: this.persistentSound,
      isUserInteracted: this.isUserInteracted,
      hasAudioContext: !!this.audioContext,
      audioContextState: this.audioContext?.state || "none",
      hasAudioElement: !!this.audioElement,
    };
  }
}

// Export singleton instance
export const notificationSound = new NotificationSoundService();
